import { graphql } from 'app/generated/gql'
import { useEffect, useState } from 'react'
import type { Pipeline } from 'app/generated/graphql'
import { useQuery } from 'urql'
import type { PipelineType } from 'app/types/entities'
import { ACTION_CENTER_FLAG, mergeSystemPipelinesAsPipelines } from 'app/components/action-center/systemPipelines'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { usePipelineStore } from 'app/stores/StoreContext'
import { useUser } from 'app/utils'

const PIPELINES_QUERY = graphql(`
  query Pipelines {
    pipelines {
      id
      name
      orgId
      workspaceId
      stages {
        name
        color
        order
      }
    }
  }
`)

/**
 * The pipelines the sidebar lists.
 *
 * The caller must be an `observer()` (`SideBarV2` is): the Action Center gate
 * below reads MobX observables, so a plain component would keep whatever was
 * true on its first render.
 */
export function usePipelines() {
  const user = useUser()
  const store = usePipelineStore()
  const actionCenterEnabled = useFeatureToggle(ACTION_CENTER_FLAG)
  const [result, reexecute] = useQuery({
    query: PIPELINES_QUERY,
    pause: !user?.workspaceId,
    requestPolicy: 'network-only',
  })

  // System pipelines are frontend constants with no server record (see
  // systemPipelines.ts), so they are merged in rather than fetched — and only
  // for a workspace that has the Action Center, whose boards are the only place
  // they can be worked. The flag reads `false` until the toggle query resolves,
  // which is the right answer to hold in the meantime: it lists nothing extra
  // and corrects itself on the re-render.
  const graphqlPipelines = (result.data?.pipelines ?? []).map((pipeline) => ({
    ...pipeline,
    stages: pipeline.stages ?? undefined,
  })) as Pipeline[]
  const pipelines: PipelineType[] = actionCenterEnabled
    ? mergeSystemPipelinesAsPipelines(graphqlPipelines)
    : graphqlPipelines

  const refetch = () => reexecute({ requestPolicy: 'network-only' })

  return {
    pipelines,
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch,
  }
}
